import { useFlashbackPtzControl } from "../../../hooks/flashbackHooks/useFlashbackPtzControl.ts";
import { PtzEdgeDirectionPad } from "../../../components/place360/PtzEdgeDirectionPad.tsx";
import { PtzGamepadDpad } from "../../../components/place360/PtzGamepadDpad.tsx";
import { PtzZoomPanel } from "../../../components/place360/PtzZoomPanel.tsx";
import type { usePatrolLivePtzAim } from "./usePatrolLivePtzAim.ts";

type PatrolLivePtzControlBundle = ReturnType<typeof usePatrolLivePtzAim>;

type PatrolLivePtzControlsProps = {
  cameraId: string;
  enabled: boolean;
  /** PiP on 360° main (wankhede-360) vs full-frame PTZ-only camera. */
  variant?: "pip" | "fullscreen";
  /** `hikvision` | `seetong` — used when `sharedControl` is omitted. */
  vendor?: unknown;
  /** Shared PTZ state from parent (keeps PiP + main controls and click-to-aim in sync). */
  sharedControl?: PatrolLivePtzControlBundle;
};

/**
 * ONVIF PTZ controls for Digital Patrol live monitoring.
 * PiP variant matches wankhede-360 / DualCameraPreview layout.
 */
export function PatrolLivePtzControls({
  cameraId,
  enabled,
  variant = "pip",
  vendor,
  sharedControl,
}: PatrolLivePtzControlsProps) {
  const internal = useFlashbackPtzControl(cameraId, enabled && !sharedControl, {
    vendor,
  });
  const {
    ptz,
    ptzQueue,
    atLimit,
    onPtzPressStart,
    onPtzPressEnd,
    ptzZoomSliderMin,
    ptzZoomSliderMax,
    onPtzZoomCommit,
    onResetZoom,
    ready,
  } = sharedControl ?? internal;

  if (!enabled || !cameraId) return null;

  const ptzOperable = ready;

  const metrics = (
    <>
      Pan {ptz.pan.toFixed(1)}° | Tilt {ptz.tilt.toFixed(1)}° | Zoom{" "}
      {Math.round(ptz.zoom)}×
    </>
  );

  const edgePad = (
    <PtzEdgeDirectionPad
      counts={ptzQueue.counts}
      isFull={ptzQueue.isFull}
      atLimit={atLimit}
      operable={ptzOperable}
      onPressStart={onPtzPressStart}
      onPressEnd={onPtzPressEnd}
    />
  );

  const zoomPanel = (
    <PtzZoomPanel
      inPlayer
      compact
      ptz={ptz}
      ptzOperable={ptzOperable}
      sliderMin={ptzZoomSliderMin}
      sliderMax={ptzZoomSliderMax}
      onZoomCommit={onPtzZoomCommit}
      onResetZoom={onResetZoom}
      onZoomPressStart={onPtzPressStart}
      onZoomPressEnd={onPtzPressEnd}
      atLimit={{
        zoomIn: atLimit.zoomIn,
        zoomOut: atLimit.zoomOut,
      }}
    />
  );

  if (variant === "fullscreen") {
    return (
      <div
        className="ds-preview-ptz-fs-overlay patrol-live-ptz-controls"
        aria-hidden={false}
      >
        <div className="ds-preview-ptz-wheel-surface" aria-hidden />
        <div className="ds-preview-ptz-fs-metrics" aria-live="polite">
          {metrics}
        </div>
        <PtzGamepadDpad
          placement="bottom-right"
          counts={ptzQueue.counts}
          isFull={ptzQueue.isFull}
          atLimit={atLimit}
          operable={ptzOperable}
          onPressStart={onPtzPressStart}
      onPressEnd={onPtzPressEnd}
        />
        <div className="ds-preview-ptz-fs-zoom">{zoomPanel}</div>
      </div>
    );
  }

  return (
    <>
      <div className="ds-preview-pip__chrome ds-preview-pip__chrome--overlay">
        <span>PTZ</span>
        <span className="ds-preview-pip__metrics" aria-live="polite">
          {metrics}
        </span>
      </div>
      <div className="ds-preview-pip__edge-controls patrol-live-ptz-controls">
        <div className="ds-preview-pip__edge-pad-layer">{edgePad}</div>
        <div className="ds-preview-pip__pip-zoom-bar">{zoomPanel}</div>
      </div>
    </>
  );
}
